import { useState } from 'react';
import { useActivitiesSearch, type Activity } from '../hooks/useActivitiesSearch';
import SearchResultCard from './SearchResultCard';
import { MapPin, Loader2 } from 'lucide-react';

interface ActivitiesSearchProps {
  defaultLatitude?: number;
  defaultLongitude?: number;
  onSelectActivity?: (activity: any) => void;
}

export default function ActivitiesSearch({
  defaultLatitude = 48.8566, // Paris default
  defaultLongitude = 2.3522,
  onSelectActivity,
}: ActivitiesSearchProps) {
  const { results, loading, error, search, demoMode } = useActivitiesSearch();
  const [form, setForm] = useState({
    latitude: defaultLatitude,
    longitude: defaultLongitude,
    radius: 5,
    keyword: '',
    category: '',
  });

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'SIGHTSEEING', label: 'Sightseeing' },
    { value: 'TOURS', label: 'Tours' },
    { value: 'MUSEUMS', label: 'Museums' },
    { value: 'ENTERTAINMENT', label: 'Entertainment' },
    { value: 'NIGHTLIFE', label: 'Nightlife' },
    { value: 'SPORTS', label: 'Sports' },
    { value: 'WELLNESS', label: 'Wellness' },
  ];

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await search(form);
  };

  const formatPrice = (price: number) => {
    if (price === 0) return 'Free';
    return `$${price}`;
  };

  return (
    <div className="space-y-4">
      {/* Demo Mode Banner */}
      {demoMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          💡 Showing demo data. Configure Amadeus API keys in Supabase secrets for real activity search.
        </div>
      )}

      {/* Search Form */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">Find Activities</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Keyword</label>
            <input
              type="text"
              placeholder="e.g., Louvre"
              className="input-field"
              value={form.keyword}
              onChange={(e) => setForm({ ...form, keyword: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select
              className="input-field"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <MapPin className="h-3 w-3 inline mr-1" />
              Radius (km)
            </label>
            <select
              className="input-field"
              value={form.radius}
              onChange={(e) => setForm({ ...form, radius: parseInt(e.target.value) })}
            >
              {[1, 2, 3, 5, 10, 20, 50].map((r) => (
                <option key={r} value={r}>{r} km</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MapPin className="h-4 w-4" />
              )}
              Search
            </button>
          </div>
        </div>
      </form>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">{results.length} activities found</p>
          {results.slice(0, 10).map((activity, index) => (
            <SearchResultCard
              key={activity.id || index}
              type="activity"
              title={activity.name}
              subtitle={activity.description?.slice(0, 100) || 'No description'}
              price={activity.price}
              currency="USD"
              details={[
                activity.rating ? `${activity.rating} ⭐` : null,
                activity.category || null,
                activity.price === 0 ? 'Free' : formatPrice(activity.price),
              ].filter(Boolean) as string[]}
              onSelect={() => onSelectActivity?.(activity)}
              selectLabel="Add to Itinerary"
            />
          ))}
        </div>
      )}

      {results.length === 0 && !loading && form.keyword && (
        <p className="text-center text-gray-500 py-4">No activities found. Try different keywords.</p>
      )}
    </div>
  );
}
